import React, { useState, useEffect } from 'react';
import {
  FileText,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Filter,
  Loader2,
  Eye
} from 'lucide-react';
import api from '../../api';
import toast from 'react-hot-toast';

interface ChangeRequest {
  id: number;
  project_id: number;
  source_type: 'transcript' | 'notes' | 'document' | 'manual';
  source_reference: string | null;
  parsed_changes: any[];
  status: 'pending' | 'approved' | 'rejected' | 'partially_approved' | 'processing';
  confidence_score: number;
  affected_steps: number[];
  affected_sops: number[];
  created_at: string;
  creator?: {
    id: number;
    name: string;
    email: string;
  };
  processor?: {
    id: number;
    name: string;
    email: string;
  };
  processed_at?: string;
}

interface ChangeRequestListProps {
  projectId: number;
  onSelectChange?: (changeRequest: ChangeRequest) => void;
  refreshTrigger?: number;
}

const ChangeRequestList: React.FC<ChangeRequestListProps> = ({
  projectId,
  onSelectChange,
  refreshTrigger
}) => {
  const [changeRequests, setChangeRequests] = useState<ChangeRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sourceTypeFilter, setSourceTypeFilter] = useState<string>('all');

  useEffect(() => {
    fetchChangeRequests();
  }, [projectId, statusFilter, sourceTypeFilter, refreshTrigger]);

  const fetchChangeRequests = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (sourceTypeFilter !== 'all') params.append('sourceType', sourceTypeFilter);

      const response = await api.get(
        `/api/analysis/change-requests/${projectId}?${params.toString()}`
      );

      console.log('Fetched change requests:', response.data.changeRequests);
      setChangeRequests(response.data.changeRequests);
    } catch (error: any) {
      console.error('Fetch change requests error:', error);
      toast.error('Failed to load change requests');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'approved':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'rejected':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'partially_approved':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'processing':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <Clock className="w-4 h-4" />;
      case 'approved':
        return <CheckCircle className="w-4 h-4" />;
      case 'rejected':
        return <XCircle className="w-4 h-4" />;
      case 'processing':
        return <Loader2 className="w-4 h-4 animate-spin" />;
      default:
        return <AlertCircle className="w-4 h-4" />;
    }
  };

  const getConfidenceColor = (score: number) => {
    if (score >= 0.8) return 'text-green-600';
    if (score >= 0.6) return 'text-yellow-600';
    return 'text-red-600';
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';

    try {
      // Parse date - handle both ISO string and timestamp
      const date = new Date(dateString);
      const now = new Date();

      // Debug logging (remove after fixing)
      console.log('[formatDate] Input:', dateString);
      console.log('[formatDate] Parsed date:', date.toISOString());
      console.log('[formatDate] Current time:', now.toISOString());

      // Validate date
      if (isNaN(date.getTime())) {
        console.error('[formatDate] Invalid date:', dateString);
        return 'Invalid date';
      }

      // Calculate difference in milliseconds
      const diffMs = now.getTime() - date.getTime();

      console.log('[formatDate] Diff (ms):', diffMs);

      // Handle future dates (clock skew)
      if (diffMs < 0) {
        console.warn('[formatDate] Date is in future:', dateString);
        return 'Just now';
      }

      const diffMinutes = Math.floor(diffMs / (1000 * 60));
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffDays = Math.floor(diffHours / 24);

      console.log('[formatDate] Diff:', { minutes: diffMinutes, hours: diffHours, days: diffDays });

      if (diffMinutes < 1) return 'Just now';
      if (diffMinutes < 60) return `${diffMinutes}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays < 7) return `${diffDays}d ago`;

      // Format as readable date for older entries
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
      });
    } catch (error) {
      console.error('[formatDate] Error:', error, dateString);
      return 'Error';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-3 text-gray-600">Loading change requests...</span>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Header with Filters */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">Change Requests</h2>
          <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-medium">
            {changeRequests.length} total
          </span>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Filters:</span>
          </div>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="partially_approved">Partially Approved</option>
          </select>

          {/* Source Type Filter */}
          <select
            value={sourceTypeFilter}
            onChange={(e) => setSourceTypeFilter(e.target.value)}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">All Sources</option>
            <option value="transcript">Transcript</option>
            <option value="notes">Notes</option>
            <option value="document">Document</option>
            <option value="manual">Manual</option>
          </select>
        </div>
      </div>

      {/* List */}
      <div className="divide-y divide-gray-200">
        {changeRequests.length === 0 ? (
          <div className="p-12 text-center">
            <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 text-sm">No change requests found</p>
            <p className="text-gray-400 text-xs mt-1">Upload a transcript or document to get started</p>
          </div>
        ) : (
          changeRequests.map((request) => (
            <div
              key={request.id}
              className="p-6 hover:bg-gray-50 transition-colors cursor-pointer"
              onClick={() => onSelectChange && onSelectChange(request)}
            >
              <div className="flex items-start justify-between">
                {/* Left Section */}
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    {/* Status Badge */}
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${getStatusColor(request.status)}`}>
                      {getStatusIcon(request.status)}
                      {request.status.replace('_', ' ').toUpperCase()}
                    </span>

                    {/* Source Type */}
                    <span className="px-2.5 py-1 bg-gray-100 text-gray-700 rounded text-xs font-medium">
                      {request.source_type.toUpperCase()}
                    </span>

                    {/* Confidence Score */}
                    <span className={`text-xs font-semibold ${getConfidenceColor(request.confidence_score)}`}>
                      {(request.confidence_score * 100).toFixed(0)}% confidence
                    </span>
                  </div>

                  {/* Reference */}
                  {request.source_reference && (
                    <p className="text-sm font-medium text-gray-900 mb-1">
                      {request.source_reference}
                    </p>
                  )}

                  {/* Changes Summary */}
                  <p className="text-sm text-gray-600 mb-2">
                    {request.parsed_changes.length} change{request.parsed_changes.length !== 1 ? 's' : ''} detected
                    {request.affected_steps.length > 0 && ` • ${request.affected_steps.length} step${request.affected_steps.length !== 1 ? 's' : ''} affected`}
                  </p>

                  {/* Creator & Date */}
                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    <span>Created by {request.creator?.name || 'Unknown'}</span>
                    <span>•</span>
                    <span>{formatDate(request.created_at)}</span>
                    {request.processed_at && (
                      <>
                        <span>•</span>
                        <span>Processed {formatDate(request.processed_at)}</span>
                      </>
                    )}
                  </div>
                </div>

                {/* Right Section - View Button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onSelectChange && onSelectChange(request);
                  }}
                  className="ml-4 p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                  title="View Details"
                >
                  <Eye className="w-5 h-5" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ChangeRequestList;
